import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useModalStore } from '@/store/modalStore';

import * as D from '../dialogStyles';

const LoginIcon = () => (
	<svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden>
		<circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="1.75" />
		<path d="M5 20v-1.2a7 7 0 0 1 14 0V20" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
	</svg>
);

const Modal = () => {
	const { t } = useTranslation();
	const { message, isOpen, closeModal, type, clickModal, buttonText } = useModalStore();
	const panelRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!isOpen) return;

		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') closeModal();
		};

		document.addEventListener('keydown', onKeyDown);
		return () => document.removeEventListener('keydown', onKeyDown);
	}, [isOpen, closeModal]);

	if (!isOpen) return null;

	const handleBackdropClick = (e: React.MouseEvent) => {
		if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
			closeModal();
		}
	};

	const isTwoButton = type === 'twoBtn' || type === 'register';
	const isLogin = type === 'login';
	const isCompact = !isTwoButton;

	const primaryLabel = buttonText ? t(buttonText) : t('common.actions.confirm');
	const confirmLabel =
		type === 'register' ? t('common.actions.register') : buttonText ? t(buttonText) : t('common.actions.confirm');

	return (
		<D.Overlay onClick={handleBackdropClick} role="presentation">
			<D.Panel ref={panelRef} $compact={isCompact} role="alertdialog" aria-modal="true" aria-labelledby="global-modal-message">
				<D.Body $centered={isCompact}>
					{isLogin && (
						<D.IconBadge aria-hidden>
							<LoginIcon />
						</D.IconBadge>
					)}
					{isTwoButton && (
						<D.IconBadge $tone="neutral" aria-hidden>
							!
						</D.IconBadge>
					)}
					<D.BrandLabel>{t('common.brand.acronym')}</D.BrandLabel>
					<D.Message id="global-modal-message" $emphasis={isLogin}>
						{message}
					</D.Message>
				</D.Body>
				<D.Footer
					$stack={isTwoButton}
					$fullWidth={isCompact}
					data-stack={isTwoButton ? 'true' : undefined}
					data-full-width={isCompact ? 'true' : undefined}
				>
					{isTwoButton ? (
						<>
							<D.DialogOutlineButton type="button" onClick={closeModal}>
								{t('common.actions.cancel')}
							</D.DialogOutlineButton>
							<D.DialogPrimaryButton type="button" onClick={clickModal}>
								{confirmLabel}
							</D.DialogPrimaryButton>
						</>
					) : (
						<D.DialogPrimaryButton type="button" onClick={isLogin ? clickModal : closeModal}>
							{primaryLabel}
						</D.DialogPrimaryButton>
					)}
				</D.Footer>
			</D.Panel>
		</D.Overlay>
	);
};

export default Modal;
